import api from './api';

export interface Setting {
  _id?: string;
  key: string;
  value: string;
  description?: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Removed complex interfaces - just use basic Setting interface

class SettingsApi {
  private baseURL = '/settings';

  // Get all settings
  async getAllSettings(): Promise<Setting[]> {
    const response = await api.get(this.baseURL);
    return response.data;
  }

  // Get setting by key
  async getSetting(key: string): Promise<Setting | null> {
    const response = await api.get(`${this.baseURL}/${key}`);
    return response.data;
  }

  // Create or update setting
  async setSetting(key: string, value: string, description?: string, type: 'string' | 'number' | 'boolean' | 'json' = 'string'): Promise<Setting> {
    const response = await api.post(this.baseURL, {
      key,
      value,
      description,
      type,
    });
    return response.data;
  }

  // Update setting
  async updateSetting(key: string, value: string, description?: string, type: 'string' | 'number' | 'boolean' | 'json' = 'string'): Promise<Setting> {
    const response = await api.put(`${this.baseURL}/${key}`, {
      value,
      description,
      type,
    });
    return response.data;
  }

  // Delete setting
  async deleteSetting(key: string): Promise<{ success: boolean }> {
    const response = await api.delete(`${this.baseURL}/${key}`);
    return response.data;
  }

  // Initialize default settings
  async initializeDefaultSettings(): Promise<{ message: string }> {
    const response = await api.post(`${this.baseURL}/initialize`);
    return response.data;
  }

  // Domain check job methods
  async startDomainCheck(): Promise<{ jobId: string; message: string }> {
    const response = await api.post(`${this.baseURL}/check-domains-indexed`);
    return response.data;
  }

  async getDomainCheckStatus(jobId: string): Promise<DomainCheckJob | null> {
    const response = await api.get(`${this.baseURL}/check-domains-indexed/${jobId}`);
    return response.data;
  }

  async getAllDomainCheckJobs(limit?: number): Promise<DomainCheckJob[]> {
    // If limit is not provided or 0, don't send limit param to get all records
    const params = limit && limit > 0 ? { limit } : {};
    const response = await api.get(`${this.baseURL}/check-domains-indexed`, {
      params,
    });
    return response.data;
  }

  async getJobErrors(
    jobId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{ errors: Array<{ domain: string; error: string; timestamp?: string }>; total: number; page: number; limit: number }> {
    const response = await api.get(`${this.baseURL}/check-domains-indexed/${jobId}/errors`, {
      params: { page, limit },
    });
    return response.data;
  }

  async stopDomainCheck(jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseURL}/check-domains-indexed/${jobId}/stop`);
    return response.data;
  }

  // Enemy domains died
  async checkEnemyDomainsDied(): Promise<{
    success: boolean;
    message: string;
    data?: string[];
    processingResult?: {
      processed: number;
      spamLinksUpdated: number;
      redirectRulesDeleted: number;
      errors: string[];
    };
  }> {
    const response = await api.post(`${this.baseURL}/enemy-domains-died`);
    return response.data;
  }

  // Server Management methods
  async getAllServers(): Promise<ServerConfig[]> {
    const response = await api.get(`${this.baseURL}/servers`);
    return response.data;
  }

  async getServerById(id: string): Promise<ServerConfig> {
    const response = await api.get(`${this.baseURL}/servers/${id}`);
    return response.data;
  }

  async addServer(config: { domain: string; dirweb_x_api_key: string; label?: string }): Promise<ServerConfig> {
    const response = await api.post(`${this.baseURL}/servers`, config);
    return response.data;
  }

  async updateServer(id: string, updates: Partial<{ domain: string; dirweb_x_api_key: string; label?: string }>): Promise<ServerConfig> {
    const response = await api.put(`${this.baseURL}/servers/${id}`, updates);
    return response.data;
  }

  async deleteServer(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${this.baseURL}/servers/${id}`);
    return response.data;
  }

  // Multi-server statistics
  async getMultiServerStatistics(): Promise<MultiServerStatisticsResponse> {
    const response = await api.get(`${this.baseURL}/multi-server-statistics`);
    return response.data;
  }

  // Sync servers
  async syncAllServers(): Promise<SyncAllServersResponse> {
    const response = await api.post(`${this.baseURL}/servers/sync-all`);
    return response.data;
  }
}

export interface ServerStatistics {
  indexed: number;
  notIndexed: number;
  redirected: number;
  active: number;
  total: number;
}

export interface ServerStatisticsResult {
  serverId: string;
  domain: string;
  label?: string;
  success: boolean;
  data?: ServerStatistics;
  error?: string;
  errorCode?: 'timeout' | 'unauthorized' | 'server_error' | 'network_error' | 'invalid_response';
  timestamp?: string;
  responseTime?: number;
}

export interface MultiServerStatisticsResponse {
  success: boolean;
  total: ServerStatistics;
  servers: ServerStatisticsResult[];
  timestamp: string;
  totalResponseTime?: number;
}

export interface SyncResult {
  serverId: string;
  domain: string;
  label?: string;
  success: boolean;
  error?: string;
  responseTime?: number;
}

export interface SyncAllServersResponse {
  success: boolean;
  total: number;
  successful: number;
  failed: number;
  results: SyncResult[];
  timestamp: string;
}

export interface ServerConfig {
  id: string;
  domain: string;
  dirweb_x_api_key: string;
  label?: string; // Human-readable label, e.g., "Server 1 - Production"
  createdAt?: string;
  updatedAt?: string;
}

export interface DomainCheckJob {
  id?: string;
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'stopped';
  totalDomains: number;
  completedDomains: number;
  failedDomains: number;
  errors?: Array<{
    domain: string;
    error: string;
    timestamp: string;
  }>;
  logFilePath?: string | null; // Log file path
  startedAt?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const settingsApi = new SettingsApi();
